/**
 * Drill-down from a dashboard tile.
 *
 * The SAME drill as a custom report's: `useReportDrill` decides which reports
 * a click may land on, builds the landing and draws the tray. What is the
 * dashboard's here is only WHERE those go:
 *
 *  - The drill starts from the tile's EFFECTIVE spec — its own report with the
 *    dashboard's period and filter bar applied. That is the question behind the
 *    number the reader clicked; starting from the stored spec would land on
 *    rows that do not add up to it.
 *  - The menu is portalled to the page. A grid tile is CSS-transformed, and a
 *    transformed ancestor re-anchors `position: fixed`, so a menu left inside
 *    the tile opens in the wrong place.
 *  - The tray is portalled into the dashboard's drill row, the full-width row
 *    the grid opens directly under the tile's row (`layoutWithDrill`), so the
 *    reader keeps the tile they clicked in view.
 *
 * One drill is open at a time, and it is the dashboard's state — never the
 * definition's: reading a drill is not an edit, and nothing here is saved.
 */
import type { OnDrill } from '@clockwise/report-kit';
import type { ReportModel, ReportSpec } from '@clockwise/semantic-core';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { DrillStep } from '@/components/reports/drill-chain';
import { useReportDrill } from '@/components/reports/report-drill';
import { analyticsApi } from '@/lib/api';

/** The one drill open on a dashboard, as a tile sees it. */
export interface TileDrillContext {
  /** This tile's chain — absent unless the open drill is this tile's. */
  chain?: readonly DrillStep[];
  onChainChange: (chain: DrillStep[]) => void;
  /** The drill row's element, once the grid has drawn it. */
  host: HTMLElement | null;
  /** May build reports — "Open as its own report". */
  mayBuild: boolean;
}

/** Stable, so a tile with no drill open does not replay an empty chain every render. */
const NO_CHAIN: readonly DrillStep[] = [];

const TRANSPORT = {
  runQuery: (spec: ReportSpec, signal?: AbortSignal) => analyticsApi.query(spec, signal),
};

export function useTileDrill({
  models,
  source,
  drill,
}: {
  models: readonly ReportModel[] | undefined;
  /** The tile: its saved report's id when linked, its title, and the spec it runs. */
  source: { id?: string; name: string; spec: ReportSpec | null };
  /** Absent while editing, and on a tile that cannot drill: no drill at all. */
  drill?: TileDrillContext;
}): { onDrill?: OnDrill; portals: ReactNode } {
  const result = useReportDrill({
    models,
    source: drill ? source : { ...source, spec: null },
    transport: TRANSPORT,
    chain: drill?.chain ?? NO_CHAIN,
    onChainChange: (chain) => drill?.onChainChange(chain),
    mayBuild: drill?.mayBuild ?? false,
  });

  if (!drill) return { portals: null };
  return {
    onDrill: result.onDrill,
    portals: (
      <>
        {result.menu &&
          createPortal(
            // The library's colour tokens travel with the menu: outside a tile
            // there is no `.rk-scope` to inherit them from, and the panel would
            // paint see-through.
            <div className="rk-scope report-kit-tokens">{result.menu}</div>,
            document.body,
          )}
        {result.tray &&
          drill.host &&
          createPortal(
            <div className="rk-scope report-kit-scope flex min-h-0 flex-1 flex-col">
              {result.tray}
            </div>,
            drill.host,
          )}
      </>
    ),
  };
}
